// src/pages/finance/payroll/Payslip.jsx
// Printable & Editable payslip modal — visually matches the Samriddhi Anveshana Pvt Ltd
// payslip template and supports:
// 1. Editable Finance View (during Generated/Draft stages, with live Half-Day & Absent LOP recalculation)
// 2. Read-Only Finalized/Employee View (with Attendance/LOP details, PDF download & printing)

import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import { generatePayslipPDF } from "../../../utils/payrollPdfExport";

const COMPANY_NAME = "Samriddhi Anveshana Pvt Ltd";
const BRAND_NAME = "Samriddhi Anveshana";
const COMPANY_ADDRESS = "705, Workafella Cyber Crown HiTech City, Hyderabad Telangana, India";
const LOGO_URL = "/assets/samriddhi-logo.png";
const EMPLOYER_SIGNATURE_URL = "/assets/employer-signature.png";

function PdfIcon({ size = 15 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  );
}

function PrintIcon({ size = 15 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
      />
    </svg>
  );
}

function numberToWordsIndian(num) {
  num = Math.round(Number(num) || 0);
  if (num === 0) return "Zero";

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen",
    "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const twoDigits = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const threeDigits = (n) =>
    n < 100 ? twoDigits(n) : ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigits(n % 100) : "");

  let n = num;
  let result = "";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;

  if (crore) result += threeDigits(crore) + " Crore ";
  if (lakh) result += threeDigits(lakh) + " Lakh ";
  if (thousand) result += threeDigits(thousand) + " Thousand ";
  if (hundred) result += threeDigits(hundred);

  return result.trim();
}

function fmtDate(value) {
  if (!value) return "NA";
  if (value?.toDate) return value.toDate().toLocaleDateString("en-GB");
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleDateString("en-GB");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "NA";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function LogoMonogram() {
  const initials = BRAND_NAME.split(" ")
    .filter((w) => w[0] && w[0] === w[0].toUpperCase())
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  return (
    <div
      style={{
        height: 56, width: 56, borderRadius: 10, background: "#1a6f4e",
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, fontSize: "1.1rem", flexShrink: 0,
      }}
      title={`${COMPANY_NAME}`}
    >
      {initials || "•"}
    </div>
  );
}

export default function Payslip({
  payment,
  employee,
  isEditable = false,
  onSave = null,
  onFinalize = null,
  onClose,
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [signatureFailed, setSignatureFailed] = useState(false);
  const [editing, setEditing] = useState(isEditable);
  const [saving, setSaving] = useState(false);

  // Form State initialized from payment document
  const [formData, setFormData] = useState({
    employeeName: "",
    employeeId: "",
    customEmployeeId: "",
    designation: "",
    department: "",
    joiningDate: "",
    bankAccountNo: "",
    bankName: "",
    ifscCode: "",
    pfNumber: "",
    workedDays: 30,
    basic: 0,
    hra: 0,
    specialAllowance: 0,
    bonusIncentive: 0,
    travelAllowance: 0,
    otherAllowance: 0,
    pf: 0,
    esi: 0,
    professionalTax: 0,
    incomeTax: 0,
    absentDays: 0,
    halfDayCount: 0,
    perDaySalary: 0,
    fullDayLopDeduction: 0,
    halfDayDeduction: 0,
    attendanceLopDeduction: 0,
    otherDeductions: 0,
  });

  useEffect(() => {
    if (payment) {
      const basic = Number(payment.basic ?? payment.baseAmount ?? 0);
      const hra = Number(payment.hra ?? 0);
      const specialAllowance = Number(payment.specialAllowance ?? 0);
      const bonusIncentive = Number(payment.bonusIncentive ?? 0);
      const travelAllowance = Number(payment.travelAllowance ?? 0);
      const otherAllowance = Number(payment.otherAllowance ?? 0);
      const workedDays = payment.workedDays ?? 30;

      const monthlyGross = basic + hra + specialAllowance + bonusIncentive + travelAllowance + otherAllowance;
      const salaryDays = 30;
      const calculatedPerDay = payment.perDaySalary ?? (salaryDays > 0 ? monthlyGross / salaryDays : 0);
      const absentDays = Number(payment.absentDays ?? 0);
      const halfDayCount = Number(payment.halfDayCount ?? 0);
      const fullDayLopDeduction = Number(payment.fullDayLopDeduction ?? Math.round(absentDays * calculatedPerDay));
      const halfDayDeduction = Number(payment.halfDayDeduction ?? Math.round(halfDayCount * (calculatedPerDay * 0.5)));
      const attendanceLopDeduction = Number(payment.attendanceLopDeduction ?? payment.lopDeduction ?? (fullDayLopDeduction + halfDayDeduction));

      const resolvedEmpName = payment.employeeName || employee?.displayName || employee?.name || "";
      const resolvedEmpId = employee?.employeeId || employee?.empId || employee?.customId || payment.customEmployeeId || payment.employeeId || "";
      const resolvedDesignation = employee?.designation || employee?.role || payment.designation || payment.role || "Staff";
      const resolvedDepartment = employee?.department || employee?.dept || payment.department || "Engineering";
      const resolvedJoiningDate = employee?.joiningDate || employee?.dateOfJoining || employee?.internshipStartDate || payment.joiningDate || "";
      const resolvedBankAcc = payment.bankAccountNo && payment.bankAccountNo !== "NA" ? payment.bankAccountNo : (employee?.accountNumber || employee?.bankAccountNo || employee?.bankAccountNumber || "");
      const resolvedPfNumber = payment.pfNumber && payment.pfNumber !== "NA" ? payment.pfNumber : (employee?.pfNumber || employee?.pfNo || employee?.uan || "");

      setFormData({
        employeeName: resolvedEmpName,
        employeeId: payment.employeeId || employee?.uid || "",
        customEmployeeId: resolvedEmpId,
        designation: resolvedDesignation,
        department: resolvedDepartment,
        joiningDate: resolvedJoiningDate,
        bankAccountNo: resolvedBankAcc,
        bankName: payment.bankName || employee?.bankName || "",
        ifscCode: payment.ifscCode || employee?.ifscCode || "",
        pfNumber: resolvedPfNumber,
        workedDays,
        basic,
        hra,
        specialAllowance,
        bonusIncentive,
        travelAllowance,
        otherAllowance,
        pf: Number(payment.pf ?? payment.pfDeduction ?? 0),
        esi: Number(payment.esi ?? 0),
        professionalTax: Number(payment.professionalTax ?? 0),
        incomeTax: Number(payment.incomeTax ?? 0),
        absentDays,
        halfDayCount,
        perDaySalary: Math.round(calculatedPerDay * 100) / 100,
        fullDayLopDeduction,
        halfDayDeduction,
        attendanceLopDeduction,
        otherDeductions: Number(payment.otherDeductions ?? 0),
      });
      setEditing(isEditable);
    }
  }, [payment, employee, isEditable]);

  // Real-time asynchronous subdocument loader for bank_details
  useEffect(() => {
    async function loadBankSubdoc() {
      const uid = employee?.uid || employee?.id || payment?.employeeId;
      if (!uid) return;
      try {
        const snap = await getDoc(doc(db, "users", uid, "bank_details", "main"));
        if (snap.exists()) {
          const b = snap.data();
          setFormData((prev) => ({
            ...prev,
            bankAccountNo: (b.accountNumber || b.bankAccountNo || prev.bankAccountNo || "NA"),
            bankName: (b.bankName || prev.bankName || "NA"),
            ifscCode: (b.ifscCode || prev.ifscCode || "NA"),
            pfNumber: (b.pfNumber || b.uan || prev.pfNumber || "NA"),
          }));
        }
      } catch (err) {
        console.warn("Could not fetch bank_details subdoc", err);
      }
    }
    loadBankSubdoc();
  }, [employee, payment]);

  if (!payment) return null;

  // Real-time calculations
  const basic = Number(formData.basic || 0);
  const hra = Number(formData.hra || 0);
  const specialAllowance = Number(formData.specialAllowance || 0);
  const bonusIncentive = Number(formData.bonusIncentive || 0);
  const travelAllowance = Number(formData.travelAllowance || 0);
  const otherAllowance = Number(formData.otherAllowance || 0);

  const totalEarnings = basic + hra + specialAllowance + bonusIncentive + travelAllowance + otherAllowance;
  const salaryDays = 30;
  const perDaySalary = salaryDays > 0 ? totalEarnings / salaryDays : 0;

  const absentDays = Number(formData.absentDays || 0);
  const halfDayCount = Number(formData.halfDayCount || 0);
  const fullDayLopDeduction = Math.round(absentDays * perDaySalary);
  const halfDayDeduction = Math.round(halfDayCount * (perDaySalary * 0.5));
  const calculatedAttendanceLop = fullDayLopDeduction + halfDayDeduction;
  const attendanceLopDeduction = formData.attendanceLopDeduction !== undefined ? Number(formData.attendanceLopDeduction) : calculatedAttendanceLop;

  const pf = Number(formData.pf || 0);
  const esi = Number(formData.esi || 0);
  const pt = Number(formData.professionalTax || 0);
  const incomeTax = Number(formData.incomeTax || 0);
  const otherDeductions = Number(formData.otherDeductions || 0);

  const totalDeductions = pf + esi + pt + incomeTax + attendanceLopDeduction + otherDeductions;
  const netPay = totalEarnings - totalDeductions;

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "halfDayCount" || field === "absentDays" || field === "basic" || field === "hra" || field === "specialAllowance" || field === "bonusIncentive" || field === "travelAllowance" || field === "otherAllowance") {
        const b = field === "basic" ? Number(value || 0) : Number(prev.basic || 0);
        const h = field === "hra" ? Number(value || 0) : Number(prev.hra || 0);
        const sa = field === "specialAllowance" ? Number(value || 0) : Number(prev.specialAllowance || 0);
        const bi = field === "bonusIncentive" ? Number(value || 0) : Number(prev.bonusIncentive || 0);
        const ta = field === "travelAllowance" ? Number(value || 0) : Number(prev.travelAllowance || 0);
        const oa = field === "otherAllowance" ? Number(value || 0) : Number(prev.otherAllowance || 0);
        const currentGross = b + h + sa + bi + ta + oa;
        const currentPerDay = currentGross / 30;

        const countHalf = field === "halfDayCount" ? Number(value || 0) : Number(prev.halfDayCount || 0);
        const countAbsent = field === "absentDays" ? Number(value || 0) : Number(prev.absentDays || 0);
        const autoHalf = Math.round(countHalf * (currentPerDay * 0.5));
        const autoAbsent = Math.round(countAbsent * currentPerDay);
        updated.perDaySalary = Math.round(currentPerDay * 100) / 100;
        updated.halfDayDeduction = autoHalf;
        updated.fullDayLopDeduction = autoAbsent;
        updated.attendanceLopDeduction = autoHalf + autoAbsent;
        updated.workedDays = Math.max(0, Math.round((30 - countAbsent - (countHalf * 0.5)) * 10) / 10);
      }
      return updated;
    });
  };

  const getPayload = () => ({
    ...formData,
    basic,
    hra,
    specialAllowance,
    bonusIncentive,
    travelAllowance,
    otherAllowance,
    pf,
    esi,
    professionalTax: pt,
    incomeTax,
    absentDays,
    halfDayCount,
    perDaySalary: Math.round(perDaySalary * 100) / 100,
    fullDayLopDeduction,
    halfDayDeduction,
    attendanceLopDeduction,
    otherDeductions,
    gross: totalEarnings,
    deductions: totalDeductions,
    net: netPay,
    workedDays: formData.workedDays === "" ? 30 : Number(formData.workedDays),
    bankAccountNo: formData.bankAccountNo || "NA",
    bankName: formData.bankName || "NA",
    ifscCode: formData.ifscCode || "NA",
    pfNumber: formData.pfNumber || "NA",
  });

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(getPayload());
      setEditing(false);
    } catch (err) {
      alert("Error saving payslip: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!onFinalize) return;
    const confirm = window.confirm(
      "Are you sure you want to finalize this payslip? Once finalized, values are locked and ready for payment."
    );
    if (!confirm) return;

    setSaving(true);
    try {
      await onFinalize(getPayload());
      setEditing(false);
    } catch (err) {
      alert("Error finalizing payslip: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isFinalized = payment.status === "Finalized" || payment.payslipStatus === "Finalized";
  const isPaid = payment.status === "Paid" || payment.paymentStatus === "Paid";
  const isShared = payment.status === "Shared";
  const canEdit = onSave && !isFinalized && !isPaid && !isShared;

  const currentStatus = payment.status || (isShared ? "Shared" : isPaid ? "Paid" : isFinalized ? "Finalized" : "Generated");

  return (
    <div style={overlayStyle} onClick={onClose}>
      <style>{PRINT_STYLE}</style>
      <div style={modalShellStyle} onClick={(e) => e.stopPropagation()}>
        
        {/* Top Toolbar */}
        <div className="payslip-toolbar" style={toolbarStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                padding: "0.25rem 0.65rem",
                borderRadius: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                backgroundColor: isShared ? "#dcfce7" : isPaid ? "#dbeafe" : isFinalized ? "#fef3c7" : "#f3f4f6",
                color: isShared ? "#166534" : isPaid ? "#1e40af" : isFinalized ? "#92400e" : "#374151",
                border: "1px solid currentColor",
              }}
            >
              Status: {currentStatus}
            </span>

            {canEdit && !editing && (
              <button
                className="btn-secondary"
                style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                onClick={() => setEditing(true)}
              >
                ✏️ Edit Values
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.5rem" }}>
            {editing ? (
              <>
                <button
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                  style={{ background: "#16a34a", borderColor: "#16a34a" }}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
                {onFinalize && (
                  <button
                    className="btn-primary"
                    onClick={handleFinalize}
                    disabled={saving}
                    style={{ background: "#2563eb", borderColor: "#2563eb" }}
                  >
                    Finalize Payslip
                  </button>
                )}
                <button
                  className="btn-secondary"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  Cancel Edit
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-primary"
                  onClick={() => generatePayslipPDF({ ...payment, ...getPayload() }, employee)}
                  style={{
                    fontSize: "0.82rem",
                    padding: "0.4rem 0.9rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <PdfIcon size={15} />
                  <span>Download PDF</span>
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => window.print()}
                  style={{
                    fontSize: "0.82rem",
                    padding: "0.4rem 0.8rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <PrintIcon size={15} />
                  <span>Print</span>
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "#f1f5f9",
                borderRadius: "6px",
                width: 32,
                height: 32,
                fontSize: "1.1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Payslip Print Area */}
        <div className="payslip-print-area" style={sheetStyle}>
          
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1.2rem 1.5rem 0.8rem",
              borderBottom: "2px solid #1a6f4e",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {logoFailed ? (
                <LogoMonogram />
              ) : (
                <img
                  src={LOGO_URL}
                  alt={BRAND_NAME}
                  style={{ height: 50, objectFit: "contain" }}
                  onError={() => setLogoFailed(true)}
                />
              )}
              <div>
                <h1 style={{ margin: 0, fontSize: "1.25rem", color: "#1a6f4e", fontWeight: 800 }}>
                  {COMPANY_NAME}
                </h1>
                <p style={{ margin: "2px 0 0", fontSize: "0.75rem", color: "#475569" }}>
                  {COMPANY_ADDRESS}
                </p>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "0.3rem 0.8rem",
                  background: "#1a6f4e",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  borderRadius: "4px",
                  letterSpacing: "0.05em",
                }}
              >
                PAYSLIP
              </span>
              <div style={{ fontSize: "0.82rem", color: "#334155", fontWeight: 600, marginTop: "4px" }}>
                {payment.month} {payment.year}
              </div>
            </div>
          </div>

          {/* Payslip Main Table */}
          <table style={tableStyle}>
            <tbody>
              {/* Row 1: Employee Name & Emp ID */}
              <tr>
                <td style={{ ...cellStyle, width: "25%", fontWeight: 600 }}>Employee Name</td>
                <td style={{ ...cellStyle, width: "25%", fontWeight: 700, color: "#111827" }}>
                  {formData.employeeName || employee?.displayName || employee?.name || "Employee"}
                </td>
                <td style={{ ...cellStyle, width: "25%", fontWeight: 600 }}>Emp ID</td>
                <td style={{ ...cellStyle, width: "25%", fontWeight: 700, color: "#1e3a8a" }}>
                  {formData.customEmployeeId || employee?.employeeId || employee?.empId || employee?.customId || payment.customEmployeeId || payment.employeeId || "—"}
                </td>
              </tr>

              {/* Row 2: Designation & Department */}
              <tr>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Designation</td>
                <td style={cellStyle}>{formData.designation || employee?.designation || employee?.role || payment.designation || "Staff"}</td>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Department</td>
                <td style={cellStyle}>{formData.department || employee?.department || employee?.dept || payment.department || "Engineering"}</td>
              </tr>

              {/* Row 3: Worked Days & Date of Joining */}
              <tr>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Worked Days</td>
                <td style={{ ...cellStyle, fontWeight: 700, color: "#166534" }}>
                  {editing ? (
                    <input
                      type="number"
                      step="0.5"
                      min={0}
                      max={31}
                      style={{ ...inputStyle, width: "70px" }}
                      value={formData.workedDays}
                      onChange={(e) => handleFieldChange("workedDays", e.target.value)}
                    />
                  ) : (
                    formData.workedDays ?? "30"
                  )}
                </td>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Date of Joining</td>
                <td style={cellStyle}>{fmtDate(formData.joiningDate || employee?.joiningDate || employee?.dateOfJoining || employee?.internshipStartDate || payment.joiningDate)}</td>
              </tr>

              {/* Row 4: Bank A/C No. & PF Number */}
              <tr>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Bank A/C No.</td>
                <td style={{ ...cellStyle, fontWeight: 600, color: "#111827" }}>
                  {editing ? (
                    <input
                      type="text"
                      style={inputStyle}
                      value={formData.bankAccountNo}
                      onChange={(e) => handleFieldChange("bankAccountNo", e.target.value)}
                      placeholder="e.g. 123456789012"
                    />
                  ) : (
                    formData.bankAccountNo || employee?.accountNumber || employee?.bankAccountNo || payment.bankAccountNo || "NA"
                  )}
                </td>
                <td style={{ ...cellStyle, fontWeight: 600 }}>PF Number</td>
                <td style={{ ...cellStyle, fontWeight: 600, color: "#111827" }}>
                  {editing ? (
                    <input
                      type="text"
                      style={inputStyle}
                      value={formData.pfNumber}
                      onChange={(e) => handleFieldChange("pfNumber", e.target.value)}
                      placeholder="e.g. MH/BAN/12345/678"
                    />
                  ) : (
                    formData.pfNumber || employee?.pfNumber || employee?.pfNo || employee?.uan || payment.pfNumber || "NA"
                  )}
                </td>
              </tr>

              {/* Earnings / Deductions Header */}
              <tr>
                <td style={{ ...cellStyle, fontWeight: 700, background: "#f3f4f6", color: "#1B4B31" }} colSpan={2}>
                  EARNINGS
                </td>
                <td style={{ ...cellStyle, fontWeight: 700, background: "#f3f4f6", color: "#991b1b" }} colSpan={2}>
                  DEDUCTIONS
                </td>
              </tr>

              {/* Basic & PF */}
              <tr>
                <td style={cellStyle}>Basic Salary / Stipend</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.basic}
                      onChange={(e) => handleFieldChange("basic", e.target.value)}
                    />
                  ) : (
                    inr(basic)
                  )}
                </td>
                <td style={cellStyle}>Provident Fund (PF)</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.pf}
                      onChange={(e) => handleFieldChange("pf", e.target.value)}
                    />
                  ) : (
                    inr(pf)
                  )}
                </td>
              </tr>

              {/* HRA & PT */}
              <tr>
                <td style={cellStyle}>House Rent Allowance (HRA)</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.hra}
                      onChange={(e) => handleFieldChange("hra", e.target.value)}
                    />
                  ) : (
                    inr(hra)
                  )}
                </td>
                <td style={cellStyle}>Professional Tax (PT)</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.professionalTax}
                      onChange={(e) => handleFieldChange("professionalTax", e.target.value)}
                    />
                  ) : (
                    inr(pt)
                  )}
                </td>
              </tr>

              {/* Special Allowance & Income Tax */}
              <tr>
                <td style={cellStyle}>Special Allowance</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.specialAllowance}
                      onChange={(e) => handleFieldChange("specialAllowance", e.target.value)}
                    />
                  ) : (
                    inr(specialAllowance)
                  )}
                </td>
                <td style={cellStyle}>Income Tax (TDS)</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.incomeTax}
                      onChange={(e) => handleFieldChange("incomeTax", e.target.value)}
                    />
                  ) : (
                    inr(incomeTax)
                  )}
                </td>
              </tr>

              {/* Travel Allowance & ESI */}
              <tr>
                <td style={cellStyle}>Conveyance / Travel Allowance</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.travelAllowance}
                      onChange={(e) => handleFieldChange("travelAllowance", e.target.value)}
                    />
                  ) : (
                    inr(travelAllowance)
                  )}
                </td>
                <td style={cellStyle}>ESI</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.esi}
                      onChange={(e) => handleFieldChange("esi", e.target.value)}
                    />
                  ) : (
                    inr(esi)
                  )}
                </td>
              </tr>

              {/* Bonus / Incentives & Attendance/LOP Deduction */}
              <tr>
                <td style={cellStyle}>Bonus / Incentives</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.bonusIncentive}
                      onChange={(e) => handleFieldChange("bonusIncentive", e.target.value)}
                    />
                  ) : (
                    inr(bonusIncentive)
                  )}
                </td>
                <td style={cellStyle}>
                  <div>
                    <strong style={{ color: "#111827", fontSize: "0.85rem" }}>Attendance / LOP Deduction</strong>
                    <div style={{ fontSize: "0.78rem", color: "#374151", marginTop: "4px" }}>
                      {editing ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                          <div>
                            <div style={{ color: "#4b5563" }}>Absent/LOP Days:</div>
                            <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "2px" }}>
                              <input
                                type="number"
                                min={0}
                                style={{ width: "50px", padding: "2px 4px", fontSize: "0.82rem" }}
                                value={formData.absentDays}
                                onChange={(e) => handleFieldChange("absentDays", e.target.value)}
                              />
                              <span style={{ fontSize: "0.74rem", color: "#6b7280" }}>(@100% per-day)</span>
                            </div>
                          </div>
                          <div>
                            <div style={{ color: "#4b5563" }}>Unpaid Half-Days:</div>
                            <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "2px" }}>
                              <input
                                type="number"
                                min={0}
                                style={{ width: "50px", padding: "2px 4px", fontSize: "0.82rem" }}
                                value={formData.halfDayCount}
                                onChange={(e) => handleFieldChange("halfDayCount", e.target.value)}
                              />
                              <span style={{ fontSize: "0.74rem", color: "#6b7280" }}>(@50% per-day)</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "3px" }}>
                          <div>
                            <div>Absent/LOP Days:</div>
                            <div style={{ fontWeight: 700, fontSize: "0.88rem", color: absentDays > 0 ? "#991b1b" : "#374151" }}>
                              {absentDays}{" "}
                              <span style={{ fontWeight: 400, fontSize: "0.74rem", color: "#6b7280" }}>
                                (@100% per-day)
                              </span>
                            </div>
                          </div>
                          <div>
                            <div>Unpaid Half-Days:</div>
                            <div style={{ fontWeight: 700, fontSize: "0.88rem", color: halfDayCount > 0 ? "#991b1b" : "#374151" }}>
                              {halfDayCount}{" "}
                              <span style={{ fontWeight: 400, fontSize: "0.74rem", color: "#6b7280" }}>
                                (@50% per-day)
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td style={{ ...cellStyle, verticalAlign: "middle" }}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.attendanceLopDeduction}
                      onChange={(e) => handleFieldChange("attendanceLopDeduction", e.target.value)}
                    />
                  ) : (
                    <span style={{ color: attendanceLopDeduction > 0 ? "#991b1b" : "inherit", fontWeight: attendanceLopDeduction > 0 ? 700 : "normal" }}>
                      {inr(attendanceLopDeduction)}
                    </span>
                  )}
                </td>
              </tr>

              {/* Other Allowances & Other Deductions */}
              <tr>
                <td style={cellStyle}>Other Allowance</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.otherAllowance}
                      onChange={(e) => handleFieldChange("otherAllowance", e.target.value)}
                    />
                  ) : (
                    inr(otherAllowance)
                  )}
                </td>
                <td style={cellStyle}>Other Deductions</td>
                <td style={cellStyle}>
                  {editing ? (
                    <input
                      type="number"
                      style={inputStyle}
                      value={formData.otherDeductions}
                      onChange={(e) => handleFieldChange("otherDeductions", e.target.value)}
                    />
                  ) : (
                    inr(otherDeductions)
                  )}
                </td>
              </tr>

              {/* Totals */}
              <tr style={{ background: "#f9fafb" }}>
                <td style={{ ...cellStyle, fontWeight: 700, color: "#1B4B31" }}>Total Earnings :</td>
                <td style={{ ...cellStyle, fontWeight: 700, color: "#1B4B31" }}>{inr(totalEarnings)}</td>
                <td style={{ ...cellStyle, fontWeight: 700, color: "#991b1b" }}>Total Deductions :</td>
                <td style={{ ...cellStyle, fontWeight: 700, color: "#991b1b" }}>{inr(totalDeductions)}</td>
              </tr>

              {/* Net Pay */}
              <tr style={{ background: "#ecfdf5" }}>
                <td style={{ ...cellStyle, fontWeight: 800, fontSize: "0.95rem", color: "#166534" }} colSpan={2}>
                  Net Payable Salary :
                </td>
                <td style={{ ...cellStyle, fontWeight: 800, fontSize: "1.05rem", color: "#166534" }} colSpan={2}>
                  {inr(netPay)}
                </td>
              </tr>
              <tr>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Amount in Words :</td>
                <td style={{ ...cellStyle, fontStyle: "italic" }} colSpan={3}>
                  {numberToWordsIndian(netPay)} Rupees Only
                </td>
              </tr>

              {/* Payment Details info row */}
              <tr style={{ background: "#f9fafb" }}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Payment Status :</td>
                <td style={cellStyle}>
                  <span style={{ fontWeight: 700, color: isPaid ? "#16a34a" : "#ca8a04" }}>
                    {isPaid ? "Paid" : "Pending Payment"}
                  </span>
                </td>
                <td style={{ ...cellStyle, fontWeight: 600 }}>Payment Date :</td>
                <td style={cellStyle}>{payment.paidAt ? fmtDate(payment.paidAt) : (isPaid ? "Completed" : "Pending")}</td>
              </tr>

              {/* Signatures */}
              <tr>
                <td style={{ ...cellStyle, height: 60, verticalAlign: "bottom" }} colSpan={2}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Employer Signature :</div>
                  {signatureFailed ? (
                    <div style={{ fontStyle: "italic", color: "#555", fontSize: "0.8rem" }}>
                      Authorized Signatory, {COMPANY_NAME}
                    </div>
                  ) : (
                    <img
                      src={EMPLOYER_SIGNATURE_URL}
                      alt="Employer Signature"
                      style={{ height: 28 }}
                      onError={() => setSignatureFailed(true)}
                    />
                  )}
                </td>
                <td style={{ ...cellStyle, height: 60, verticalAlign: "bottom" }} colSpan={2}>
                  <div style={{ fontWeight: 700 }}>Employee Signature :</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── styles ──────────────────────────────────────────────────────── */
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  overflowY: "auto", padding: "1.5rem 1rem", zIndex: 1000,
};
const modalShellStyle = {
  background: "#fff", borderRadius: 10, width: "100%", maxWidth: 780,
  boxShadow: "0 12px 40px rgba(0,0,0,0.25)", overflow: "hidden",
};
const toolbarStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  flexWrap: "wrap", gap: "0.75rem",
  padding: "0.75rem 1.25rem", borderBottom: "1px solid #e5e7eb", background: "#f8fafc",
};
const sheetStyle = { padding: "0 0 1rem", fontFamily: "Arial, Helvetica, sans-serif", color: "#111" };
const tableStyle = { width: "calc(100% - 2.5rem)", borderCollapse: "collapse", margin: "0.5rem 1.25rem" };
const cellStyle = { border: "1px solid #cbd5e1", padding: "6px 10px", fontSize: "0.84rem" };
const inputStyle = {
  width: "100%", padding: "4px 6px", fontSize: "0.84rem",
  border: "1px solid #94a3b8", borderRadius: "4px", boxSizing: "border-box",
  background: "#fff",
};

const PRINT_STYLE = `
@media print {
  body * { visibility: hidden; }
  .payslip-print-area, .payslip-print-area * { visibility: visible; }
  .payslip-print-area { position: absolute; top: 0; left: 0; width: 100%; }
  .payslip-toolbar { display: none !important; }
}
`;
